/**
 * Geo-Clock heartbeat hook — sends periodic location updates while app is open.
 *
 * Architecture:
 *   - Every 2 minutes while the document is visible, captures GPS via browser
 *     Geolocation API and sends to POST /api/geo/heartbeat
 *   - Pauses when the tab is hidden (visibilitychange)
 *   - Also fetches pending clock suggestions from GET /api/geo/suggestions
 *   - Returns the current at-station status and any pending suggestion
 *
 * PWA constraint: This only works while the app is foregrounded. Background
 * geolocation is not available in PWAs. Push notifications handle the case
 * where the user isn't actively looking at the app.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';
import { captureLocation } from '../lib/offline';

// ── Types ────────────────────────────────────────────────────────────────

export interface GeoClockSuggestion {
  id: number;
  type: 'clock_in' | 'clock_out';
  station_id: string | null;
  station_name: string | null;
  shift_date: string | null;
  shift_start: string | null;
  shift_end: string | null;
  duration_hours: number | null;
  triggered_at: string;
  message: string | null;
}

export interface AutoActionResult {
  type: string;
  entry_id: number;
}

interface HeartbeatResponse {
  at_station: boolean;
  station_id: string | null;
  station_name: string | null;
  suggestion: { type: string; id: number } | null;
  auto_action?: AutoActionResult | null;
}

interface SuggestionsResponse {
  suggestions: GeoClockSuggestion[];
}

export interface UseGeoHeartbeatReturn {
  atStation: boolean;
  stationName: string | null;
  pendingSuggestion: GeoClockSuggestion | null;
  lastAutoAction: AutoActionResult | null;
  respondToSuggestion: (id: number, action: 'accept' | 'dismiss', extra?: Record<string, string>) => Promise<boolean>;
  refreshSuggestions: () => Promise<void>;
  clearAutoAction: () => void;
}

// ── Constants ────────────────────────────────────────────────────────────

const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000; // 2 minutes

// ── Hook ─────────────────────────────────────────────────────────────────

export function useGeoHeartbeat(enabled: boolean): UseGeoHeartbeatReturn {
  const [atStation, setAtStation] = useState(false);
  const [stationName, setStationName] = useState<string | null>(null);
  const [pendingSuggestion, setPendingSuggestion] = useState<GeoClockSuggestion | null>(null);
  const [lastAutoAction, setLastAutoAction] = useState<AutoActionResult | null>(null);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const suggestionsFailedRef = useRef(false);

  // ── Fetch pending suggestions ────────────────────────────────────────

  const refreshSuggestions = useCallback(async () => {
    if (suggestionsFailedRef.current) return;
    try {
      const data = await apiFetch<SuggestionsResponse>('/api/geo/suggestions');
      setPendingSuggestion(data.suggestions[0] ?? null);
    } catch {
      suggestionsFailedRef.current = true;
    }
  }, []);

  // ── Send heartbeat ──────────────────────────────────────────────────

  const sendHeartbeat = useCallback(async () => {
    if (!enabled) return;

    const location = await captureLocation();
    if (!location) return; // No GPS available

    try {
      const data = await apiFetch<HeartbeatResponse>('/api/geo/heartbeat', {
        method: 'POST',
        body: JSON.stringify({
          lat: location.lat,
          lng: location.lng,
          accuracy: location.accuracy,
        }),
      });

      setAtStation(data.at_station);
      setStationName(data.station_name);
      suggestionsFailedRef.current = false;

      // If auto-clock fired, surface the action to the UI
      if (data.auto_action) {
        setLastAutoAction(data.auto_action);
      } else if (data.suggestion) {
        // If the server created a new suggestion, fetch full details
        await refreshSuggestions();
      }
    } catch {
      // Non-critical: heartbeat failure doesn't affect the app
    }
  }, [enabled, refreshSuggestions]);

  // ── Respond to suggestion ────────────────────────────────────────────

  const respondToSuggestion = useCallback(
    async (id: number, action: 'accept' | 'dismiss', extra?: Record<string, string>): Promise<boolean> => {
      try {
        await apiFetch(`/api/geo/suggestion/${id}/respond`, {
          method: 'POST',
          body: JSON.stringify({ action, ...extra }),
        });
        setPendingSuggestion(null);
        return true;
      } catch {
        return false;
      }
    },
    [],
  );

  // ── Heartbeat interval with visibility management ────────────────────

  useEffect(() => {
    if (!enabled) return;

    // Fetch suggestions immediately; defer first heartbeat to the interval
    // to avoid Chrome's geolocation-without-user-gesture violation
    refreshSuggestions();

    const startInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);
    };

    const stopInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        // Resume: send immediately + restart interval
        sendHeartbeat();
        refreshSuggestions();
        startInterval();
      } else {
        stopInterval();
      }
    };

    startInterval();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [enabled, sendHeartbeat, refreshSuggestions]);

  // ── Also check for suggestions from push notification deep links ─────

  useEffect(() => {
    const url = new URL(window.location.href);
    const suggestionId = url.searchParams.get('suggestion');
    const action = url.searchParams.get('action');

    if (suggestionId && action === 'accept') {
      // Auto-accept from push notification tap — but still show banner for category selection
      refreshSuggestions();
      // Clean URL params
      url.searchParams.delete('suggestion');
      url.searchParams.delete('action');
      window.history.replaceState({}, '', url.pathname);
    }
  }, [refreshSuggestions]);

  const clearAutoAction = useCallback(() => setLastAutoAction(null), []);

  return {
    atStation,
    stationName,
    pendingSuggestion,
    lastAutoAction,
    respondToSuggestion,
    refreshSuggestions,
    clearAutoAction,
  };
}
